import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface PendingChangeOrder {
  id: string;
  version_number: number;
  created_at: string;
}

export const useChangeOrderWarning = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [pendingChangeOrders, setPendingChangeOrders] = useState<PendingChangeOrder[]>([]);

  const checkForPendingChangeOrders = useCallback(async (estimateId: string): Promise<PendingChangeOrder[]> => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('estimate_versions')
        .select('id, version_number, created_at')
        .eq('estimate_id', estimateId)
        .eq('version_type', 'change_order')
        .eq('status', 'sent')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error checking for pending change orders:', error);
        return [];
      }

      const changeOrders = data || [];
      setPendingChangeOrders(changeOrders);
      return changeOrders;
    } catch (error) {
      console.error('Error checking for pending change orders:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  const approveChangeOrders = useCallback(async (changeOrderIds: string[]): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      // Approve all pending change orders
      const { error } = await supabase
        .from('estimate_versions')
        .update({ 
          status: 'approved',
          accepted_date: new Date().toISOString()
        })
        .in('id', changeOrderIds);

      if (error) {
        console.error('Error approving change orders:', error);
        toast.error('Failed to approve change orders');
        return false;
      }

      toast.success(`Change order${changeOrderIds.length === 1 ? '' : 's'} approved successfully`);
      setPendingChangeOrders([]);
      return true;
    } catch (error) {
      console.error('Error approving change orders:', error);
      toast.error('Failed to approve change orders');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const approveChangeOrdersAndMakeMain = useCallback(async (estimateId: string, changeOrderIds: string[]): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      // Get the latest change order to make it the main version
      const latestChangeOrderId = changeOrderIds[0]; // They're ordered by created_at desc
      
      // First approve all pending change orders
      const { error: approveError } = await supabase
        .from('estimate_versions')
        .update({ 
          status: 'approved',
          accepted_date: new Date().toISOString()
        })
        .in('id', changeOrderIds);

      if (approveError) {
        console.error('Error approving change orders:', approveError);
        toast.error('Failed to approve change orders');
        return false;
      }

      // Update the estimate's main_version_id to point to the latest change order
      const { error: mainError } = await supabase
        .from('estimates')
        .update({ main_version_id: latestChangeOrderId })
        .eq('id', estimateId);

      if (mainError) {
        console.error('Error updating estimate main version:', mainError);
        toast.error('Failed to update estimate');
        return false;
      }

      toast.success('Change order approved and set as main version');
      setPendingChangeOrders([]);
      return true;
    } catch (error) {
      console.error('Error approving change orders and setting main:', error);
      toast.error('Failed to approve change orders');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    isLoading,
    pendingChangeOrders,
    checkForPendingChangeOrders,
    approveChangeOrders,
    approveChangeOrdersAndMakeMain
  };
};